import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { memo, useMemo, useState } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { PmiButton } from 'shell/components/shared/button';
import { ExpandMore } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Form, { PracticeNotAvailableVM } from './form';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { DateTime } from 'luxon';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { regexParentesi, regexSlash, regexAt } from 'shell/constants/regex';
import { PracticeDraftBEStep } from 'practice/constants/practice-management-step';

interface AccordionPracticeNotAvailableProps {
  codeEnte?: string;
  yearId?: string;
  typeRegister?: string;
  progressive?: string;
  onNext: () => void;
  maxActiveStep: number;
  disabledAll?: boolean;
  iniziativa?: string;
}

const AccordionPracticeNotAvailableComponent = ({
  codeEnte,
  yearId,
  typeRegister,
  progressive,
  onNext,
  maxActiveStep,
  disabledAll,
  iniziativa,
}: AccordionPracticeNotAvailableProps): React.ReactElement => {
  const [open, setOpen] = useState(true);

  const navigate = useNavigate();

  const insertData = useHttpClient(
    `${__PRACTICE_API_URL__}/registrazione-pratica-irricevibile/esito`,
    {
      options: { method: 'POST' },
    }
  );

  const onSubmit = useEventCallback((values: PracticeNotAvailableVM) => {
    const date = values.date?.toFormat('yyyy-MM-dd') || '';
    const hours = values.time?.toFormat('HH:mm:ss') || '';
    const timestampClosure =
      DateTime.fromFormat(`${date || ''} ${hours || ''}`, 'yyyy-MM-dd HH:mm:ss') || undefined;

    const data = {
      codiceEnte: codeEnte,
      tipoRegistro: typeRegister,
      idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
      progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
      tsChiusura: timestampClosure,
      motivoChiusura: values.reason || undefined,
      stepBozza:
        maxActiveStep > PracticeDraftBEStep.Irricevibilita
          ? maxActiveStep
          : PracticeDraftBEStep.Irricevibilita,
      idMotivoIrricevibile: values.reasonNotAvailable || undefined,
    };

    (insertData({ data }) as ResponsePromise<void>).then(() => {
      toast.success('Operazione eseguita con successo');

      navigate('/pratiche/ricerca');
    });
  });

  const formContext = useFormik<PracticeNotAvailableVM>({
    initialValues: {
      date: DateTime.now(),
      reason: '',
      time: DateTime.now(),
      reasonNotAvailable: '',
    },
    validationSchema: yup
      .object()
      .shape({
        date: yup.date().nullable().required('Campo obbligatorio').typeError('Data non valida'),
        reason: yup.string().required('Campo obbligatorio'),
        time: yup.date().nullable().required('Campo obbligatorio').typeError('Data non valida'),
        reasonNotAvailable: yup.string().required('Campo obbligatorio'),
      })
      .test('', '', (values) => {
        if (!values.reason) {
          return true;
        }
        // const regularExpDiacritic = new RegExp(onlyDiacriticRegex);

        const regularExpParentesi = new RegExp(regexParentesi);

        const regularExpSlash = new RegExp(regexSlash);

        const regularExpAt = new RegExp(regexAt);

        if (
          regularExpParentesi.test(values.reason) ||
          regularExpSlash.test(values.reason) ||
          regularExpAt.test(values.reason)
        ) {
          return new yup.ValidationError(
            'Il campo non deve contenere parentesi, slash e chiocciola',
            undefined,
            'reason'
          );
        }

        return true;
      }),
    onSubmit,
  });

  const disabled = useMemo(() => {
    if (disabledAll) {
      return true;
    }

    // if (iniziativa === TypeInitiative.Anpr || iniziativa === TypeInitiative.ElixForm) {
    //   return true;
    // }

    return false;
  }, [disabledAll, iniziativa]);

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        setOpen(!open);
      }}
    >
      <AccordionSummary
        aria-controls="accordion-practice-not-available-content"
        id="accordion-practice-not-available-header"
        expandIcon={<ExpandMore />}
      >
        <Typography>Pratica Irricevibile</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Form
          onChange={formContext.setFieldValue}
          disabled={disabled}
          values={formContext.values}
          errors={formContext.errors}
          touched={formContext.touched}
        />
        <div className="pmi-pt-3">
          <PmiButton
            variant="outlined"
            color="cdmBrandThird"
            onClick={() => {
              if (disabledAll) {
                onNext();

                return;
              }
              formContext.handleSubmit();
            }}
            disabled={disabled}
            id="confirm"
          >
            Conferma
          </PmiButton>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

AccordionPracticeNotAvailableComponent.displayName = 'Practice not available';

const AccordionPracticeNotAvailable = memo(AccordionPracticeNotAvailableComponent);

export default AccordionPracticeNotAvailable;
